import { useEffect, useState } from "react";
import { Loader2, Headset } from "lucide-react";
import { toast } from "sonner";
import { Drawer } from "@/components/layout/Drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Field, DrawerSection } from "@/components/forms/fields";
import { vaAgenciesService } from "@/services/resourceServices";
import { formatApiErrorDetail } from "@/services/api";
import { isOwnerRole } from "@/lib/permissions/permissions";
import { useAuth } from "@/hooks/useAuth";

const EMPTY = { name: "", status: "active", owner_name: "", owner_email: "", warehouse_id: "" };

export function VaAgencyDrawer({ open, onOpenChange, record, warehouses = [], onSaved }) {
  const { user: currentUser } = useAuth();
  const isOwner = isOwnerRole(currentUser?.role);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!record;

  useEffect(() => {
    if (open) {
      setForm(
        record
          ? { name: record.name || "", status: record.status || "active", owner_name: "", owner_email: "", warehouse_id: record.warehouse_id || "" }
          : EMPTY
      );
    }
  }, [open, record]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const nameError = !form.name.trim() ? "Agency name is required" : "";
  const warehouseError = isOwner && !isEdit && !form.warehouse_id ? "Select a warehouse" : "";
  const canSave = !nameError && !warehouseError && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      let res;
      if (isEdit) {
        res = await vaAgenciesService.update(record.id, { name: form.name.trim(), status: form.status });
        toast.success("Agency updated");
      } else {
        const body = { name: form.name.trim(), status: form.status };
        if (isOwner) {
          body.warehouse_id = form.warehouse_id;
        }
        if (form.owner_email.trim()) {
          body.owner_email = form.owner_email.trim();
          body.owner_name = form.owner_name.trim() || form.name.trim();
        }
        res = await vaAgenciesService.create(body);
        toast.success("Agency created");
      }
      onOpenChange(false);
      onSaved?.(res);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      icon={Headset}
      title={isEdit ? "Edit VA Agency" : "New VA Agency"}
      description={isEdit ? "Update agency details." : "Create a VA agency and optionally invite its owner."}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="agency-drawer-cancel">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!canSave} data-testid="agency-drawer-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Save changes" : "Create agency"}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        <DrawerSection title="Agency details">
          {isOwner && !isEdit && (
            <Field label="Warehouse" required error={warehouseError}
                   hint="Which warehouse organization does this agency work for?">
              <Select value={form.warehouse_id || undefined} onValueChange={set("warehouse_id")}>
                <SelectTrigger data-testid="agency-warehouse-select"><SelectValue placeholder="Select a warehouse" /></SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (<SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </Field>
          )}
          <Field label="Agency Name" required error={nameError}>
            <Input value={form.name} onChange={(e) => set("name")(e.target.value)} placeholder="Peak VA Services" data-testid="agency-name-input" />
          </Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={set("status")}>
              <SelectTrigger data-testid="agency-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        </DrawerSection>

        {!isEdit && (
          <DrawerSection title="Agency owner (optional)">
            <p className="text-xs text-muted-foreground">
              Invite the agency owner now, or add owners, managers and staff later from the Users module.
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Owner Name">
                <Input value={form.owner_name} onChange={(e) => set("owner_name")(e.target.value)} placeholder="Sam Rivera" data-testid="agency-owner-name-input" />
              </Field>
              <Field label="Owner Email">
                <Input type="email" value={form.owner_email} onChange={(e) => set("owner_email")(e.target.value)} placeholder="owner@peakva.com" data-testid="agency-owner-email-input" />
              </Field>
            </div>
          </DrawerSection>
        )}
      </div>
    </Drawer>
  );
}
